import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { Row, Col, Card, CardHeader, Button, Input } from "reactstrap"
import { saveChargeLimitHoursConfiguration } from "../../apollo/server"
import { useMutation, gql } from "@apollo/client"
import { Alert } from "reactstrap/lib"

interface IProps {
  startHour: number
  endHour: number
}

// 配達料の上限(店舗ごとの「配達料上限」)を適用する時間帯。
// この時間帯の外では上限を外し、料率どおりの配達料を請求する(深夜・早朝の運用)。
const ChargeLimitHours: React.FC<IProps> = props => {
  const { t } = useTranslation()
  const [startHour, setStartHour] = useState<string>(String(props.startHour ?? 6))
  const [endHour, setEndHour] = useState<string>(String(props.endHour ?? 20))
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage, setErrorMessage] = useState("")

  const [save, { loading, error }] = useMutation(gql`${saveChargeLimitHoursConfiguration}`, {
    onCompleted: () => setSuccessMessage(t("Saved") || "保存しました"),
    onError: e => {
      setErrorMessage(e.message || t("ErrorMessage"))
      console.log(e)
    },
  })
  if (error) return <p>{t("Error")}</p>

  const start = Number(startHour)
  const end = Number(endHour)
  const isValid = Number.isInteger(start) && Number.isInteger(end) && start >= 0 && end <= 24 && start < end

  function submit(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.preventDefault()
    if (loading || !isValid) return
    save({ variables: { startHour: start, endHour: end } })
  }

  const onDismiss = () => {
    setSuccessMessage("")
    setErrorMessage("")
  }

  return (
    <Row className="mt-3">
      <div className="col">
        <Card className="shadow">
          <CardHeader className="border-0">
            <h3 className="mb-0">配達料上限の適用時間帯</h3>
            <small className="text-muted">
              この時間帯のご注文にのみ、店舗ごとに設定した「配達料上限」が適用されます。
              時間帯の外では上限を外し、料率どおりの配達料になります。
            </small>
          </CardHeader>

          <Row className="ml-3 mr-3" style={{ display: "flex", alignItems: "center" }}>
            <Col md="10">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Input
                  type="number"
                  min={0}
                  max={24}
                  value={startHour}
                  onChange={e => setStartHour(e.target.value)}
                  className="form-control-alternative"
                  style={{ maxWidth: 110 }}
                />
                <span>時 　〜　</span>
                <Input
                  type="number"
                  min={0}
                  max={24}
                  value={endHour}
                  onChange={e => setEndHour(e.target.value)}
                  className="form-control-alternative"
                  style={{ maxWidth: 110 }}
                />
                <span>時</span>
              </div>
              {isValid ? (
                <small className="text-muted">
                  {`${start}時〜${end}時のご注文は上限あり。${end}時〜翌${start}時のご注文は上限なし（料率どおり）になります。`}
                </small>
              ) : (
                <small className="text-danger">0〜24の整数で、開始より後の時刻を終了に指定してください。</small>
              )}
            </Col>
            <Col md="2">
              <Button
                className="btn-block mb-2"
                type="button"
                color="primary"
                onClick={submit}
                size="lg"
                disabled={!isValid}
              >
                {loading ? t("Saving") : t("Save")}
              </Button>
            </Col>
          </Row>
          <Row className="mx-3" style={{ display: "flex", justifyContent: "flex-end" }}>
            <Col md="6">
              <Alert color="success" isOpen={!!successMessage} toggle={onDismiss}>
                <span className="alert-inner--text" style={{ whiteSpace: "pre-wrap" }}>
                  <strong>{t("Success")}! </strong>
                  {successMessage}
                </span>
              </Alert>
              <Alert color="danger" isOpen={!!errorMessage} toggle={onDismiss}>
                <span className="alert-inner--text">
                  <strong>{t("Danger")}!</strong> {errorMessage}
                </span>
              </Alert>
            </Col>
          </Row>
        </Card>
      </div>
    </Row>
  )
}
export default ChargeLimitHours
